export interface ConcatenationFormulaResult {
  error: string | null;
  legacyValue: string;
  preview: string;
}

export interface FormulaInsertionResult {
  formula: string;
  cursor: number;
}

export interface ActiveAttributeSearch {
  start: number;
  query: string;
}

export interface ConcatenationAttributeDropdownOption {
  label: string;
  value: string | number;
  dataType?: string | null;
  persistentName?: string | null;
}

export interface AssociatedSubjectFieldDropdownSource {
  id: string | number;
  displayName?: string | null;
  associatedSubject?: {
    label?: string | null;
    persistentName?: string | null;
  } | null;
  dataType?: {
    label?: string | null;
  } | null;
}

export function combineConcatenationAttributeOptions(
  attributeOptions: ConcatenationAttributeDropdownOption[],
  associatedSubjectFields: AssociatedSubjectFieldDropdownSource[],
  excludedTableDefinitionId?: string | number | null
): ConcatenationAttributeDropdownOption[] {
  const excludedId = excludedTableDefinitionId == null ? null : String(excludedTableDefinitionId);
  const optionsByTableDefinitionId = new Map<string, ConcatenationAttributeDropdownOption>();

  attributeOptions.forEach((option) => {
    const optionId = String(option.value);
    if (!option.label || !optionId || optionId === excludedId) return;
    optionsByTableDefinitionId.set(optionId, option);
  });

  associatedSubjectFields.forEach((field) => {
    const optionId = String(field.id);
    const label = field.displayName || field.associatedSubject?.label || '';
    if (!label || !optionId || optionId === excludedId) return;

    optionsByTableDefinitionId.set(optionId, {
      label,
      value: field.id,
      dataType: field.dataType?.label || null,
      persistentName: field.associatedSubject?.persistentName || field.associatedSubject?.label || label,
    });
  });

  return Array.from(optionsByTableDefinitionId.values()).sort((first, second) =>
    first.label.localeCompare(second.label)
  );
}

export function legacyConcatenationToFormula(value?: string | null) {
  if (!value) return '';
  return value.split(',').join('');
}

export function parseConcatenationFormula(
  formula: string,
  availableAttributes: string[] = []
): ConcatenationFormulaResult {
  if (!formula) {
    return {
      error: 'Add at least one attribute or text value.',
      legacyValue: '',
      preview: '',
    };
  }

  const normalizedAttributes = new Set(availableAttributes.map((attribute) => attribute.trim().toLowerCase()));
  const legacyTokens: string[] = [];
  const previewTokens: string[] = [];
  let literalText = '';

  const flushLiteralText = () => {
    if (!literalText) return;
    legacyTokens.push(...Array.from(literalText));
    previewTokens.push(literalText);
    literalText = '';
  };

  for (let index = 0; index < formula.length; index += 1) {
    const character = formula[index];

    if (character === ',') {
      return {
        error: 'A comma cannot be used as text because commas separate saved parameters.',
        legacyValue: '',
        preview: '',
      };
    }

    if (character === '}') {
      return {
        error: 'Remove the unmatched closing curly bracket.',
        legacyValue: '',
        preview: '',
      };
    }

    if (character === '{') {
      flushLiteralText();
      const closingBracketIndex = formula.indexOf('}', index + 1);
      if (closingBracketIndex < 0) {
        return {
          error: 'Finish the attribute reference by selecting a suggestion.',
          legacyValue: '',
          preview: '',
        };
      }

      const reference = formula.slice(index + 1, closingBracketIndex).trim();
      const referencedAttributes = reference.split('|').map((attribute) => attribute.trim()).filter(Boolean);

      if (!referencedAttributes.length) {
        return { error: 'Attribute references cannot be empty.', legacyValue: '', preview: '' };
      }

      const unknownAttribute = referencedAttributes.find(
        (attribute) => normalizedAttributes.size > 0 && !normalizedAttributes.has(attribute.toLowerCase())
      );
      if (unknownAttribute) {
        return {
          error: `Unknown attribute: ${unknownAttribute}. Select it from the suggestions.`,
          legacyValue: '',
          preview: '',
        };
      }

      legacyTokens.push(`{${reference}}`);
      previewTokens.push(referencedAttributes.join(' or '));
      index = closingBracketIndex;
      continue;
    }

    literalText += character;
  }

  flushLiteralText();

  return {
    error: null,
    legacyValue: legacyTokens.join(','),
    preview: previewTokens.join(''),
  };
}

export function getActiveAttributeSearch(formula: string, cursor: number): ActiveAttributeSearch | null {
  let activeStart = -1;

  for (let index = 0; index < Math.min(cursor, formula.length); index += 1) {
    const character = formula[index];
    if (character === '{') activeStart = index;
    if (character === '}') activeStart = -1;
  }

  if (activeStart < 0) return null;
  return {
    start: activeStart,
    query: formula.slice(activeStart + 1, cursor),
  };
}

export function replaceActiveAttributeSearch(
  formula: string,
  cursor: number,
  attribute: string
): FormulaInsertionResult {
  const search = getActiveAttributeSearch(formula, cursor);
  if (!search) return { formula, cursor };

  const nextClosingBrace = formula.indexOf('}', cursor);
  const nextDelimiterCandidates = [formula.indexOf(',', cursor), formula.indexOf(')', cursor)].filter(
    (index) => index >= 0
  );
  const nextDelimiter = nextDelimiterCandidates.length ? Math.min(...nextDelimiterCandidates) : formula.length;
  const replacementEnd = nextClosingBrace >= 0 && nextClosingBrace < nextDelimiter ? nextClosingBrace + 1 : cursor;
  const reference = `{${attribute}}`;

  return {
    formula: `${formula.slice(0, search.start)}${reference}${formula.slice(replacementEnd)}`,
    cursor: search.start + reference.length,
  };
}

export function insertFormulaText(
  formula: string,
  cursor: number,
  text: string,
  cursorOffset = text.length
): FormulaInsertionResult {
  const insertionPosition = Math.max(0, Math.min(cursor, formula.length));

  return {
    formula: `${formula.slice(0, insertionPosition)}${text}${formula.slice(insertionPosition)}`,
    cursor: insertionPosition + cursorOffset,
  };
}
